import React, { useEffect, useMemo, useState } from "react";

import GraphCenterHoverCard from "./GraphCenterHoverCard";
import { getCenterNodeInitials } from "./relationshipGraphUtils";
import {
  hideRingsHoverTip,
  moveRingsHoverTip,
  showRingsHoverTip,
} from "./rings-hover-tooltip";
import {
  buildMeddpiccRingsLegendText,
  clampLevel,
  formatMeddKeyDisplay,
  getCoverageMatrixDotColor,
  MAPPED_UNASSESSED_DOT_COLOR,
  MEDD_AVATAR_BG,
} from "../account-relationships/relationshipUtils";

function getMeddBadgeWidth(key) {
  return Math.max(String(key || "").length * 7 + 10, 24);
}

const RL_BAND_MODERATE = "#eab308";

const RING_STYLES = [
  { rl: 5, fill: "#dcfce7", stroke: "#86efac", strokeW: 1.5 },
  { rl: 4, fill: "#f0fdf4", stroke: "#bbf7d0", strokeW: 1 },
  { rl: 3, fill: "#fef9c3", stroke: "#fde047", strokeW: 1.5 },
  { rl: 2, fill: "#fffbeb", stroke: "#fde68a", strokeW: 1 },
  { rl: 1, fill: "#fef2f2", stroke: "#fca5a5", strokeW: 1.5 },
];

const GEOMETRY = {
  panel: {
    cx: 148,
    cy: 142,
    vw: 296,
    vh: 300,
    nodeR: 13,
    fontSize: 8,
    youR: 17,
    unknownR: 126,
  },
  modal: {
    cx: 270,
    cy: 270,
    vw: 540,
    vh: 540,
    nodeR: 18,
    fontSize: 11,
    youR: 26,
    unknownR: 200,
  },
};

function getRingGeometry(mode) {
  const base = GEOMETRY[mode] || GEOMETRY.panel;
  const step = (base.unknownR - base.youR) / 5.5;
  const rings = [5, 4, 3, 2, 1].map((rl, index) => ({
    rl,
    r: Math.round(base.youR + step * (index + 1)),
    ...RING_STYLES[index],
  }));
  return { ...base, rings, unknownR: base.unknownR };
}

function getDotBorderColor(rlLevel) {
  if (rlLevel === null || rlLevel === undefined) {
    return MAPPED_UNASSESSED_DOT_COLOR;
  }
  return getCoverageMatrixDotColor(rlLevel);
}

function getShortName(name) {
  const parts = String(name || "Contact")
    .split(" ")
    .filter(Boolean);
  if (parts.length <= 1) return parts[0] || "Contact";
  return `${parts[0]} ${parts
    .slice(1)
    .map(part => `${part[0]}.`)
    .join("")}`;
}

function getInitials(name) {
  return (
    String(name || "Contact")
      .split(" ")
      .filter(Boolean)
      .map(part => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function truncateLabel(value, maxLength) {
  const text = String(value || "");
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 3))}...`;
}

export function RelationshipRingsLegend({
  variant = "panel",
  considerMeddpiccRole = false,
}) {
  if (variant === "modal") {
    return (
      <>
        <div className="rings-legend rings-legend--modal">
          <div className="ring-leg">
            <div
              className="ring-leg-dot"
              style={{ background: "#fff", borderColor: "#16a34a" }}
            />{" "}
            RL 4–5 Strong
          </div>
          <div className="ring-leg">
            <div
              className="ring-leg-dot"
              style={{ background: "#fff", borderColor: RL_BAND_MODERATE }}
            />{" "}
            RL 2–3 Moderate
          </div>
          <div className="ring-leg">
            <div
              className="ring-leg-dot"
              style={{ background: "#fff", borderColor: "#ef4444" }}
            />{" "}
            RL 0–1 Weak
          </div>
          <div className="ring-leg" title="Unassessed">
            <div
              className="ring-leg-dot"
              style={{ background: "#fff", borderColor: "#94a3b8" }}
            />{" "}
            RL ?
          </div>
          <div className="ring-leg">
            <div
              className="ring-leg-dot ring-leg-dot--dashed"
              style={{
                background: "#fff",
                borderColor: "#94a3b8",
                opacity: 0.55,
              }}
            />
            Not yet mapped
          </div>
        </div>
        {considerMeddpiccRole ? (
          <p className="rings-medd-legend">
            <strong>MEDDPICC:</strong> {buildMeddpiccRingsLegendText()}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <div className="rings-legend rings-legend--panel">
      <div className="ring-leg">
        <div
          className="ring-leg-dot"
          style={{ background: "#fff", borderColor: "#16a34a" }}
        />{" "}
        Strong
      </div>
      <div className="ring-leg">
        <div
          className="ring-leg-dot"
          style={{ background: "#fff", borderColor: RL_BAND_MODERATE }}
        />{" "}
        Moderate
      </div>
      <div className="ring-leg">
        <div
          className="ring-leg-dot"
          style={{ background: "#fff", borderColor: "#ef4444" }}
        />{" "}
        Weak
      </div>
      <div className="ring-leg" title="Unassessed">
        <div
          className="ring-leg-dot"
          style={{ background: "#fff", borderColor: "#94a3b8" }}
        />{" "}
        RL ?
      </div>
    </div>
  );
}

export function RelationshipRingsGraph({
  contacts = [],
  mode = "panel",
  considerMeddpiccRole = false,
  onMappedContactClick,
  userName,
  centerUserStakeholder,
}) {
  const geometry = useMemo(() => getRingGeometry(mode), [mode]);
  const spokeCount = Math.max(1, contacts.length);
  const [youHovered, setYouHovered] = useState(false);

  useEffect(() => () => hideRingsHoverTip(), []);

  if (contacts.length === 0) {
    return null;
  }

  const { cx, cy, vw, vh, nodeR, fontSize, youR, unknownR, rings } = geometry;
  const youInitials = getCenterNodeInitials(userName);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <svg
        className={`t3-rings-svg t3-rings-svg--${mode}`}
        viewBox={`0 0 ${vw} ${vh}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Relationship rings view"
      >
        <circle
          cx={cx}
          cy={cy}
          r={unknownR}
          fill="#f8fafc"
          stroke="#cbd5e1"
          strokeWidth="1.5"
          strokeDasharray="6,4"
        />
        {[...rings].reverse().map(ring => (
          <circle
            key={ring.rl}
            cx={cx}
            cy={cy}
            r={ring.r}
            fill={ring.fill}
            stroke={ring.stroke}
            strokeWidth={ring.strokeW}
            opacity="0.45"
          />
        ))}
        <g
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setYouHovered(true)}
          onMouseLeave={() => setYouHovered(false)}
        >
          <circle
            cx={cx}
            cy={cy}
            r={youR + 1}
            fill="none"
            stroke="#9333ea"
            strokeWidth="2.5"
            strokeDasharray="4,3"
          />
          <circle cx={cx} cy={cy} r={youR} fill="#1e293b" />
          <text
            x={cx}
            y={cy + 4}
            textAnchor="middle"
            fontSize={fontSize - 1}
            fill="#fff"
            fontWeight="700"
            className="t3-l2-you-lbl"
          >
            {youInitials}
          </text>
        </g>

        {contacts.map((contact, index) => {
          const angle = (index / spokeCount) * Math.PI * 2 - Math.PI / 2;
          const isMapped = contact.isMapped !== false;
          const rlInt =
            contact.rlLevel !== null && contact.rlLevel !== undefined
              ? clampLevel(Math.round(Number(contact.rlLevel)))
              : null;
          const ring =
            rlInt !== null
              ? rings.find(item => item.rl === rlInt) || rings[rings.length - 1]
              : null;
          const distance = ring ? ring.r : unknownR;
          const nx = cx + distance * Math.cos(angle);
          const ny = cy + distance * Math.sin(angle);
          const dotColor = getDotBorderColor(contact.rlLevel);
          const faded = !isMapped;
          const spokeEnd = Math.max(0, distance - nodeR - 4);
          const spokeX = cx + spokeEnd * Math.cos(angle);
          const spokeY = cy + spokeEnd * Math.sin(angle);
          const labelAbove =
            Math.sin(angle) < 0 && distance < youR + nodeR + 13 + 4;
          let nameY = labelAbove ? ny - nodeR - 6 : ny + nodeR + 13;
          if (nameY > vh - 16) nameY = ny - nodeR - 6;
          if (nameY < 14) nameY = ny + nodeR + 13;
          const meddKey = contact.meddKey || "UN";

          return (
            <g
              key={contact.id}
              opacity={faded ? 0.55 : 1}
              style={{
                cursor:
                  isMapped && onMappedContactClick ? "pointer" : "default",
              }}
              onMouseEnter={event =>
                showRingsHoverTip(event, contact, { considerMeddpiccRole })
              }
              onMouseMove={moveRingsHoverTip}
              onMouseLeave={hideRingsHoverTip}
              onClick={
                isMapped && onMappedContactClick
                  ? event => {
                      event.stopPropagation();
                      hideRingsHoverTip();
                      onMappedContactClick(contact);
                    }
                  : undefined
              }
            >
              <line
                x1={cx}
                y1={cy}
                x2={spokeX}
                y2={spokeY}
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray="3,4"
                opacity={faded ? 0.55 : 1}
              />
              {!faded ? (
                <circle
                  cx={nx}
                  cy={ny + 1.5}
                  r={nodeR + 2}
                  fill="rgba(0,0,0,0.07)"
                />
              ) : null}
              {faded ? (
                <circle
                  cx={nx}
                  cy={ny}
                  r={nodeR + 2}
                  fill="#fff"
                  stroke="#94a3b8"
                  strokeWidth="2"
                  strokeDasharray="3,3"
                />
              ) : (
                <circle cx={nx} cy={ny} r={nodeR + 2} fill={dotColor} />
              )}
              <circle cx={nx} cy={ny} r={nodeR} fill="#fff" />
              {!faded ? (
                <circle
                  cx={nx}
                  cy={ny}
                  r={nodeR - 3}
                  fill={dotColor}
                  opacity="0.12"
                />
              ) : null}
              <text
                x={nx}
                y={ny + 4}
                textAnchor="middle"
                fontSize={fontSize - 0.5}
                fill={dotColor}
                fontWeight="700"
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {getInitials(contact.name)}
              </text>
              <text
                x={nx}
                y={nameY}
                textAnchor="middle"
                fontSize={fontSize}
                fill="#1e293b"
                fontWeight="600"
                opacity={faded ? 0.55 : 1}
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                {truncateLabel(
                  getShortName(contact.name),
                  mode === "modal" ? 18 : 12
                )}
              </text>
              {considerMeddpiccRole && mode === "modal" && meddKey
                ? (() => {
                    const badgeY = labelAbove ? nameY - 26 : nameY + 14;
                    const displayMeddKey = formatMeddKeyDisplay(meddKey);
                    const badgeW = getMeddBadgeWidth(displayMeddKey);
                    const badgeFill =
                      MEDD_AVATAR_BG[meddKey] || MEDD_AVATAR_BG.UN;
                    return (
                      <>
                        <rect
                          x={nx - badgeW / 2}
                          y={badgeY}
                          width={badgeW}
                          height={14}
                          rx={3}
                          fill={badgeFill}
                          opacity={faded ? 0.5 : 0.9}
                          style={{ pointerEvents: "none" }}
                        />
                        <text
                          x={nx}
                          y={badgeY + 10}
                          textAnchor="middle"
                          fontSize={9.5}
                          fill="#fff"
                          fontWeight="700"
                          opacity={faded ? 0.55 : 1}
                          style={{ pointerEvents: "none", userSelect: "none" }}
                        >
                          {displayMeddKey}
                        </text>
                      </>
                    );
                  })()
                : null}
            </g>
          );
        })}
      </svg>
      <GraphCenterHoverCard
        visible={youHovered}
        centerUserStakeholder={centerUserStakeholder}
        initials={youInitials}
        translateYOffset={-28}
      />
    </div>
  );
}
